import React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/utils/commonUtils";

interface SkeletonLoaderProps {
  className?: string;
}

/**
 * Full-screen loading state with BLS branding.
 * Awwwards-inspired: logo, minimal typography, thin indeterminate progress bar.
 */
const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({ className }) => {
  const { t } = useTranslation("common");

  return (
    <div
      className={cn(
        "fixed inset-0 z-50 flex flex-col items-center justify-center bg-background",
        className
      )}
      aria-live="polite"
      aria-busy="true"
    >
      <div className="flex flex-col items-center gap-10 px-6">
        {/* Logo – subtle fade-in */}
        <img
          src="/bls-logo.png"
          alt="BLS International"
          className="h-14 w-auto animate-in fade-in duration-500 sm:h-16"
        />

        {/* App name – minimal, refined */}
        <p className="animate-in fade-in text-xs font-medium tracking-[0.2em] text-muted-foreground duration-500 delay-150 sm:text-[13px]">
          {t("app.name").toUpperCase()}
        </p>

        {/* Indeterminate progress bar – thin, full-width track */}
        <div className="mt-2 w-full max-w-[280px] sm:max-w-[320px]">
          <div className="relative h-[2px] w-full overflow-hidden rounded-full bg-muted/60">
            <div
              className="absolute left-0 top-0 h-full w-1/4 rounded-full bg-primary animate-loader-bar"
              aria-hidden
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SkeletonLoader;
